/**
 * Convert color stop to CSS color string
 * @param {Object} colorStop - Color stop object from parser
 * @returns {string|null} CSS color string or null if invalid
 */
export function colorStopToString(colorStop) {
  if (colorStop.type === 'hex') {
    return `#${colorStop.value}`;
  } else if (colorStop.type === 'rgba') {
    return `rgba(${colorStop.value.join(',')})`;
  } else if (colorStop.type === 'rgb') {
    return `rgb(${colorStop.value.join(',')})`;
  } else if (colorStop.type === 'literal') {
    return colorStop.value;
  }
  return null;
}

/**
 * Check if a color stop is fully transparent
 * @param {Object} colorStop - Color stop object
 * @returns {boolean}
 */
function isTransparent(colorStop) {
  if (colorStop.type === 'rgba') {
    const alpha = parseFloat(colorStop.value[3]);
    return alpha === 0;
  }
  if (colorStop.type === 'literal') {
    return colorStop.value.toLowerCase() === 'transparent';
  }
  return false;
}

/**
 * Get RGB values from a color stop
 * @param {Object} colorStop - Color stop object
 * @returns {Array|null} [r, g, b] or null
 */
function getRGBValues(colorStop) {
  if (colorStop.type === 'rgb' || colorStop.type === 'rgba') {
    return [
      parseFloat(colorStop.value[0]),
      parseFloat(colorStop.value[1]),
      parseFloat(colorStop.value[2]),
    ];
  }
  if (colorStop.type === 'hex') {
    const hex = colorStop.value;
    if (hex.length === 3) {
      return [
        parseInt(hex[0] + hex[0], 16),
        parseInt(hex[1] + hex[1], 16),
        parseInt(hex[2] + hex[2], 16),
      ];
    } else if (hex.length >= 6) {
      return [
        parseInt(hex.slice(0, 2), 16),
        parseInt(hex.slice(2, 4), 16),
        parseInt(hex.slice(4, 6), 16),
      ];
    }
  }
  return null;
}

/**
 * Fix transparent color stops to avoid the "gray band" problem in Canvas gradients.
 * CSS uses premultiplied alpha interpolation, but Canvas uses straight alpha.
 * This function modifies transparent color stops to use the RGB values of adjacent
 * non-transparent colors, which simulates premultiplied alpha behavior.
 * @param {Array} colorStops - Array of color stop objects (will be modified in place)
 */
export function fixTransparentColorStops(colorStops) {
  if (!colorStops || colorStops.length < 2) return;

  // Find the RGB values to use for transparent stops
  // For each transparent stop, use the nearest non-transparent stop's RGB
  for (let i = 0; i < colorStops.length; i++) {
    if (isTransparent(colorStops[i])) {
      // Find nearest non-transparent color
      let nearestRGB = null;
      let minDistance = Infinity;

      for (let j = 0; j < colorStops.length; j++) {
        if (i !== j && !isTransparent(colorStops[j])) {
          const distance = Math.abs(i - j);
          if (distance < minDistance) {
            minDistance = distance;
            nearestRGB = getRGBValues(colorStops[j]);
          }
        }
      }

      // Update the transparent stop to use the nearest color's RGB with alpha 0
      if (nearestRGB) {
        colorStops[i] = {
          ...colorStops[i],
          type: 'rgba',
          value: [nearestRGB[0], nearestRGB[1], nearestRGB[2], 0],
        };
      }
    }
  }
}

/**
 * Calculate the stop position for a linear gradient
 * @param {Object} colorStop - Color stop object
 * @param {number} index - Index of the color stop
 * @param {number} totalStops - Total number of color stops
 * @param {number} distance - Total gradient distance in pixels
 * @returns {number} Stop position (0-1)
 */
export function calculateLinearStop(colorStop, index, totalStops, distance) {
  if (colorStop.length) {
    if (colorStop.length.type === 'px') {
      return parseFloat(colorStop.length.value) / distance;
    } else {
      return parseFloat(colorStop.length.value) / 100;
    }
  }
  // Default distribution
  return totalStops === 2 ? (index === 0 ? 0 : 1) : index / (totalStops - 1);
}

/**
 * Calculate the stop position for a radial gradient
 * @param {Object} colorStop - Color stop object
 * @param {number} index - Index of the color stop
 * @param {number} radius - Gradient radius
 * @returns {number} Stop position (0-1)
 */
export function calculateRadialStop(colorStop, index, radius) {
  if (colorStop.length) {
    if (colorStop.length.type === 'px') {
      return parseFloat(colorStop.length.value) / (radius * 2);
    } else {
      return parseFloat(colorStop.length.value) / 100;
    }
  }
  return index === 0 ? 0 : 1;
}

/**
 * Add color stops to a gradient object
 * @param {CanvasGradient} gradient - Canvas gradient object
 * @param {Array} colorStops - Array of color stop objects
 * @param {Function} calculateStop - Function to calculate stop position
 */
export function addColorStops(gradient, colorStops, calculateStop) {
  colorStops.forEach((stop, index) => {
    const position = calculateStop(stop, index);
    const colorValue = colorStopToString(stop);

    if (colorValue) {
      // Clamp position to valid range
      const clampedPosition = Math.max(0, Math.min(1, position));
      gradient.addColorStop(clampedPosition, colorValue);
    }
  });
}
